const WEBSOCKET_URL = `${import.meta.env.VITE_API_URL}/ws`;

export const WS_CLOSE_CODES = {
  // 정상 종료
  NORMAL_CLOSURE: 1000, // 정상적인 연결 종료
  GOING_AWAY: 1001, // 클라이언트가 페이지를 떠나거나, 서버가 종료되는 경우
  PROTOCOL_ERROR: 1002, // 프로토콜 오류로 인한 종료
  UNSUPPORTED_DATA: 1003, // 지원하지 않는 데이터 타입 수신

  // 비정상 종료
  NO_STATUS_RECEIVED: 1005, // 상태 코드 없이 연결 종료 (직접 사용 불가)
  ABNORMAL_CLOSURE: 1006, // 비정상적인 연결 종료 (직접 사용 불가)

  // 데이터 오류
  INVALID_PAYLOAD: 1007, // 메시지 형식 오류
  POLICY_VIOLATION: 1008, // 정책 위반
  MESSAGE_TOO_BIG: 1009, // 메시지가 너무 큼

  // 클라이언트/서버 오류
  MANDATORY_EXTENSION: 1010, // 클라이언트가 필요한 확장을 서버가 지원하지 않음
  INTERNAL_SERVER_ERROR: 1011, // 서버 내부 오류

  // TLS 오류
  TLS_HANDSHAKE_FAILED: 1015, // TLS 핸드셰이크 실패 (직접 사용 불가)

  // 사용자 정의 코드
  MANUAL_CLOSE: 4000, // 수동 종료
} as const;

export type WebSocketCloseCode =
  (typeof WS_CLOSE_CODES)[keyof typeof WS_CLOSE_CODES];

export class WebSocketClient {
  private readonly websocketUrl: string = WEBSOCKET_URL;
  private socket: WebSocket | null = null;
  private reconnectConfig = {
    baseDelay: 1000,
    maxDelay: 30000,
    maxAttempts: Infinity, // 5 -> Infinity로 변경하여 무제한 재시도
  };
  private reconnectTimer: NodeJS.Timeout | null = null;
  private reconnectAttempts = 0;

  // 이벤트 핸들러들을 저장할 프로퍼티들
  private readonly onOpen: () => void;
  private readonly onMessage: (data: any) => void;
  private readonly onClose: (event: CloseEvent) => void;
  private readonly onError: (error: Event) => void;
  private readonly onStateChange?: (
    isConnected: boolean,
    isReconnectable: boolean,
  ) => void;

  constructor(
    options: {
      websocketUrl?: string;
      handlers?: {
        onOpen?: () => void;
        onMessage?: (data: any) => void;
        onClose?: (event: CloseEvent) => void;
        onError?: (error: Event) => void;
        onStateChange?: (
          isConnected: boolean,
          isReconnectable: boolean,
        ) => void;
      };
    } = {},
  ) {
    if (options.websocketUrl) this.websocketUrl = options.websocketUrl;

    // 기본 핸들러와 사용자 정의 핸들러를 합치기
    this.onOpen =
      options.handlers?.onOpen ??
      (() => console.log("[WebSocket-Handler] Connected"));
    this.onMessage =
      options.handlers?.onMessage ??
      ((data) => console.log("[WebSocket-Handler] Received: ", data));
    this.onClose =
      options.handlers?.onClose ??
      ((event) => console.log("[WebSocket-Handler] Disconnected: ", event));
    this.onError =
      options.handlers?.onError ??
      ((error) => console.error("[WebSocket-Handler] Error: ", error));
    this.onStateChange =
      options.handlers?.onStateChange ??
      ((isConnected, isReconnectable) =>
        console.log(
          "[WebSocket-Handler] onStateChange: ",
          isConnected,
          isReconnectable,
        ));
  }

  // 정리(cleanup) 메서드 추가
  // 소멸자 대용이나 명시적으로 호출해야함.
  cleanup(): void {
    this.disconnect();
  }

  connect(): void {
    let token = null;
    const userSession = sessionStorage.getItem("user");
    if (userSession) {
      const user = JSON.parse(userSession);
      token = user.token;
    }

    if (!token) {
      console.error("[WebSocket] Error: No token found in session storage");
    }

    this.socket = new WebSocket(
      `${this.websocketUrl}?token=${encodeURIComponent(token)}`,
    );
    this.socket.onopen = () => {
      console.log("[WebSocket] Connected.");
      this.onOpen();
      this.reconnectAttempts = 0;
      this.onStateChange?.(true, this.isReconnectable());
    };
    this.socket.onmessage = (event) => {
      console.log("[WebSocket] Message: ", event);
      this.onMessage(event.data);
    };
    this.socket.onclose = (event) => {
      const closeCode = event.code as WebSocketCloseCode;
      const wasError =
        !event.wasClean || closeCode === WS_CLOSE_CODES.ABNORMAL_CLOSURE;

      console.log("[WebSocket] Disconnected by, ", {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
        hadError: wasError,
      });

      this.onClose(event);
      this.onStateChange?.(false, this.isReconnectable());

      // 비정상 종료일 경우, 재연결 시도
      if (!this.isNormalClose(closeCode)) {
        this.setReconnectConfig(closeCode);
        this.reconnect();
      }
    };
    this.socket.onerror = (error) => {
      console.error("[WebSocket] Error: ", error);
      this.onError(error);
    };
  }

  disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
    if (this.socket) {
      if (this.isConnected()) {
        this.socket.close(WS_CLOSE_CODES.MANUAL_CLOSE);
      }
      this.socket = null;
    }
  }

  reconnect(): void {
    const backoffDelay = this.calculateBackoff(this.reconnectAttempts);
    console.log(
      `재연결 시도 ${this.reconnectAttempts + 1}, ${backoffDelay}ms 후 시도`,
    );

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts += 1;
      this.connect();
    }, backoffDelay);
  }

  reconnectAgain(): void {
    console.log("재연결 시도", this);
    if (this.isConnected()) {
      console.log("[WebSocket] Already connected.");
      return;
    }

    this.reconnectAttempts = 0;
    this.reconnect();
    this.onStateChange?.(false, this.isReconnectable());
  }

  getSocket(): WebSocket | null {
    return this.socket;
  }

  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  isReconnectable(): boolean {
    return this.reconnectAttempts < this.reconnectConfig.maxAttempts;
  }

  isNormalClose(code: WebSocketCloseCode): boolean {
    // Define an array that includes all relevant close codes
    const normalCloseCodes: WebSocketCloseCode[] = [
      WS_CLOSE_CODES.NORMAL_CLOSURE,
      WS_CLOSE_CODES.GOING_AWAY,
      WS_CLOSE_CODES.MANUAL_CLOSE,
    ];
    return normalCloseCodes.includes(code);
  }

  async sendMessage(message: string): Promise<boolean> {
    if (!this.isConnected()) {
      console.error(
        "[WebSocket] Client is not connected. Cannot send message.",
      );
      return false;
    }

    try {
      this.socket!.send(message);
      return true;
    } catch (error) {
      console.error("[WebSocket] Error: Failed to send message:", error);
      return false;
    }
  }

  setReconnectConfig(closeCode: WebSocketCloseCode) {
    switch (closeCode) {
      case 1006: // 네트워크 불안정
        this.reconnectConfig.maxAttempts = Infinity;
        this.reconnectConfig.baseDelay = 500;
        this.reconnectConfig.maxDelay = 30000; // 최대 30초
        break;

      case 1011: // 서버 에러
        this.reconnectConfig.maxAttempts = Infinity;
        this.reconnectConfig.baseDelay = 2000;
        this.reconnectConfig.maxDelay = 60000; // 최대 1분
        break;

      default:
        this.reconnectConfig.maxAttempts = Infinity;
        this.reconnectConfig.baseDelay = 1000;
        this.reconnectConfig.maxDelay = 30000;
    }
  }

  calculateBackoff(attempt: number): number {
    // exponential backoff지만 maxDelay 이상으로는 증가하지 않음
    const delay = Math.min(
      this.reconnectConfig.baseDelay * Math.pow(2, Math.min(attempt, 10)), // 최대 2^10까지만 증가
      this.reconnectConfig.maxDelay,
    );

    // 무작위성을 추가하여 여러 클라이언트의 동시 재연결 방지
    const jitter = Math.random() * 0.1 * delay;
    return delay + jitter;
  }
}
